import { SETTLEMENT_STATUS_TO_COLOR, SETTLEMENTS_ENDPOINT, TENANT_SETTINGS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Settlement } from "../../../../types/settlements";
import { Box } from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { ONE_OFF_SETTLEMENT_CYCLE } from "../../../../types/contracts";
import { useTranslation } from "react-i18next";
import { formatDateTimeInline, formatNumber } from "../../../../utils";
import {
  getSettlementContractId,
  getSettlementContractLabel,
  getSettlementDisplayLabel,
  getSettlementTermEquipments,
} from "../../../../utils/settlementContractTerms";
import { TenantSettings } from "../../../../types/tenantSettings";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { useHasPermission } from "../../../../contexts/auth";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  UserDisplay,
} from "../../../../components/ui/detailView";
import { StatusChip } from "../../../../components/ui/statusChip/statusChip.component";

export const SettlementBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(SETTLEMENTS_ENDPOINT);
  const { get: getTenantSettings } = useCRUD(TENANT_SETTINGS_ENDPOINT);
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const canViewContract = useHasPermission("settlement.view_contract");

  useEffect(() => {
    const fetchSettlement = async () => {
      if (id) {
        const response = await get(id);
        setSettlement(response.data);
      }
    };

    const fetchTenantSettings = async () => {
      const response = await getTenantSettings("settings");
      if (response.data) {
        setTenantSettings(response.data);
      }
    };

    const handleSettlementUpdated = () => {
      fetchSettlement();
    };

    window.addEventListener("settlementUpdated", handleSettlementUpdated);
    fetchSettlement();
    fetchTenantSettings();
    return () => {
      window.removeEventListener("settlementUpdated", handleSettlementUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateDaysUntilReminder = (): number | null => {
    if (!settlement?.last_reminder_at || !tenantSettings?.next_reminder_available_after_days) {
      return null;
    }

    const lastReminderDate = new Date(settlement.last_reminder_at);
    const today = new Date();
    const daysSinceLastReminder = Math.floor(
      (today.getTime() - lastReminderDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysLeft = tenantSettings.next_reminder_available_after_days - daysSinceLastReminder;

    return daysLeft > 0 ? daysLeft : 0;
  };

  if (!settlement) return <DetailPage loading />;

  const contractTerm = settlement.contract_term;
  const contractTermEquipments = contractTerm
    ? getSettlementTermEquipments(contractTerm)
    : [];
  const isBrutto = Boolean(contractTerm?.contract_info?.is_brutto);
  const daysUntilReminder = calculateDaysUntilReminder();
  const statusChip = (
    <StatusChip
      label={settlement.status_display}
      value={settlement.status}
      colorMap={SETTLEMENT_STATUS_TO_COLOR}
    />
  );
  // A one-off settlement bills the machine itself, so it has no counters and
  // no positions - the page would otherwise look simply empty.
  const isSale = contractTerm?.settlement_cycle === ONE_OFF_SETTLEMENT_CYCLE;

  return (
    <DetailPage>
      <DetailHeader
        icon={<ReceiptLongOutlinedIcon />}
        title={`${t("Settlement")} - ${getSettlementDisplayLabel(settlement, t, id ?? "-")}`}
        subtitle={contractTerm?.contract_info && getSettlementContractLabel(settlement)}
        chips={
          <>
            {statusChip}
            {isSale && (
              <StatusChip
                label={t("Equipment sale")}
                color="secondary"
                variant="outlined"
                title={t("Billed once, for the equipment price - no counters and no period.")}
              />
            )}
            {isBrutto && (
              <StatusChip
                label={t("Gross (Brutto)")}
                color="warning"
                variant="outlined"
                title={t("This contract's values are stored and settled as gross (brutto) amounts")}
              />
            )}
          </>
        }
      />

      <DetailGrid>
        <DetailSection title={t("Basic Information")}>
          <DetailRow label={t("Status")} value={statusChip} />
          {isSale && (
            <DetailRow
              label={t("Equipment price")}
              value={
                contractTerm?.equipment_price != null
                  ? formatNumber(contractTerm.equipment_price)
                  : null
              }
            />
          )}
          {settlement.sale_date && <DetailRow label={t("Sale date")} value={settlement.sale_date} />}
          {settlement.show_inflation_rate_percent && (
            <DetailRow
              label={t("Inflation rate (%)")}
              value={
                settlement.inflation_rate_percent != null && settlement.inflation_rate_percent !== ""
                  ? formatNumber(settlement.inflation_rate_percent)
                  : null
              }
            />
          )}
          <DetailRow label={t("Total Calculated")} value={formatNumber(settlement.total_calculated)} />
          {settlement.total_overridden && (
            <DetailRow label={t("Total Overridden")} value={formatNumber(settlement.total_overridden)} />
          )}
          {settlement.confirmed_date && (
            <DetailRow label={t("Confirmed date")} value={formatDateTimeInline(settlement.confirmed_date)} />
          )}
          {settlement.confirmed_by && (
            <DetailRow label={t("Confirmed By")}>
              <UserDisplay user={settlement.confirmed_by} />
            </DetailRow>
          )}
          {settlement.last_reminder_at && (
            <DetailRow label={t("Last reminder at")} value={formatDateTimeInline(settlement.last_reminder_at)} />
          )}
          {daysUntilReminder !== null && (
            <DetailRow
              label={t("Next reminder can be sent after")}
              value={`${daysUntilReminder} ${daysUntilReminder === 1 ? t("day") : t("days")}`}
            />
          )}
        </DetailSection>

        {contractTerm && (
          <DetailSection title={t("Equipment Information")}>
            <DetailRow label={t("Equipment")}>
              {contractTermEquipments.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {contractTermEquipments.map((equipment, index) => (
                    <EntityReferenceLink
                      key={equipment.id ?? index}
                      entityType="equipment"
                      entityId={equipment.id}
                      label={equipment.full_name || "—"}
                      canPreview={canViewEquipment}
                      stopPropagation={false}
                    />
                  ))}
                </Box>
              ) : null}
            </DetailRow>
            <DetailRow label={t("Settlement Cycle")} value={contractTerm.settlement_cycle_display} />
            <DetailRow
              label={isBrutto ? t("Gross rent price") : t("Net rent price")}
              value={formatNumber(contractTerm.rent_price)}
            />
            {contractTerm.contract_info && (
              <>
                <DetailRow label={t("Contract Number")}>
                  <EntityReferenceLink
                    entityType="contract"
                    entityId={getSettlementContractId(settlement)}
                    label={getSettlementContractLabel(settlement)}
                    canPreview={canViewContract}
                    stopPropagation={false}
                  />
                </DetailRow>
                <DetailRow label={t("Contract Start Date")} value={contractTerm.contract_info.start_date} />
                <DetailRow label={t("Contract End Date")} value={contractTerm.contract_info.end_date} />
              </>
            )}
          </DetailSection>
        )}
      </DetailGrid>

      <DetailMetadata createdAt={settlement.created_at} updatedAt={settlement.updated_at} />
    </DetailPage>
  );
};
